/**
 * ProfilePage — 个人资料页
 *
 * 移植自原型 design/Novel-admin/src/pages/profile.jsx，profile 域已接真（goal PR13）。
 * 六个 tab：资料 / 安全 / 偏好 / 登录会话 / 通知 / API 令牌。
 *
 * 已接真（本人账户操作，非 root-only —— RBAC 是「本人」语义）：
 *  - 当前用户：侧栏卡 + 资料 tab 读 auth-store.user（GET /auth/me 在登录链路已落 store）
 *  - 改密码：PATCH /api/v1/me/password（成功后服务端撤销全会话 → 引导重新登录）
 *  - 登录会话：读 GET /me/sessions；登出单会话 DELETE /me/sessions/:id；
 *    登出所有设备 = auth logout(true)（POST /auth/logout {all_devices:true}）
 *  - 可信设备：读 GET /me/trusted-devices；撤销 DELETE /me/trusted-devices/:id
 *  - 通知偏好 / 勿扰时段：读 GET、改 PATCH /api/v1/me/notification-preferences
 *    （admin 独有表 admin_notification_prefs，本人 scope；通知类型 taxonomy + 默认值由前端 NOTIFY_ROWS 拥有）
 *
 * NOT-in-scope（dev-docs 无对应需求，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）：
 *  - 个人 API Token / SSO 解绑 / 短信·硬件密钥因子 / 改资料·头像（无端点）
 *  - 2FA 因子卡操作（重新绑定/绑定/编辑号码/重新生成/下载备用码）：dev-docs 无已登录态因子管理端点
 *    → TOTP 状态只读 auth-store 真值；未接入因子保持诚实空态 / disabled
 *  - 界面偏好（主题/密度/强调色）：app-scope-store 本地态即可（非接真）
 */

import type {
  NotificationChannelPrefs,
  SelfSessionResponse,
  TrustedDeviceResponse,
  UpdateNotificationPrefsRequest,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createElement, useEffect, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { type TablePageSize, useAppScope } from '~/app/app-scope-store';
import {
  useApps,
  useMeNotificationPrefs,
  useMeSessions,
  useMeTrustedDevices,
} from '~/data/queries';
import { logout } from '~/hooks/use-auth';
import { meApi } from '~/hooks/use-me';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { ProfileAvatar } from '~/pages/profile/ProfileAvatar';
import { TwoFactorManageDialog } from '~/pages/profile/TwoFactorManageDialog';
import { useAuthStore } from '~/stores/auth-store';
import { ConfirmDialog } from '~/ui/Dialog';
import { FormRow as SharedFormRow } from '~/ui/FormRow';
import { Section as SharedSection } from '~/ui/Section';
import { Select } from '~/ui/Select';
import { SectionSkeleton, TableSkeleton } from '~/ui/Skeleton';
import { Switch as SharedSwitch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';

/** NOT-in-scope 动作统一 title（指向计划 §五） */
const NOT_IN_SCOPE_TITLE =
  '待契约（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）';

/** 角色英文 → 侧栏卡中文徽章文案 */
const ROLE_LABEL: Record<string, string> = {
  root: '超级管理员',
  admin: 'Admin',
  operator: '运营',
  viewer: '只读',
};

// 本页 Section / FormRow / Switch 用法：照设计稿 design/Novel-admin/src/pages/profile.jsx 的薄封装，
// 逐属性还原 profile 历史尺寸（与原型 1:1）：
//   Section headPad="14px 0 12px"（头部内边距）
//   FormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={4}
//   Switch  size="sm"（30×17）
function Section(props: React.ComponentProps<typeof SharedSection>) {
  return <SharedSection headPad="14px 0 12px" {...props} />;
}
function FormRow(props: React.ComponentProps<typeof SharedFormRow>) {
  return <SharedFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={4} {...props} />;
}
function Switch(props: React.ComponentProps<typeof SharedSwitch>) {
  return <SharedSwitch size="sm" {...props} />;
}

type TabId = 'profile' | 'security' | 'pref' | 'sessions' | 'notify' | 'tokens';

interface TabDef {
  id: TabId;
  label: string;
  icon: keyof typeof Icons;
}

export default function ProfilePage() {
  const [tab, setTab] = useState<TabId>('profile');
  const user = useAuthStore((s) => s.user);
  const avatarInitial = (user?.nickname || user?.email || '?').charAt(0).toUpperCase();
  // 首屏不门控整页：默认 tab（资料）+ 安全 / 偏好 / 通知 / 令牌 都读 auth-store 或本地态，无后端列表
  // 依赖。会话 / 可信设备列表只有「登录会话」tab 才用 —— 故页面 chrome 与全部 tab 立即渲染，骨架屏下沉
  // 到 Sessions 组件内（其 query isLoading 时本地渲染骨架），避免慢的 sessions/devices 端点阻塞无关
  // tab。门控仍由真 TanStack Query isLoading 驱动，不移植原型假定时器。
  const tabs: TabDef[] = [
    { id: 'profile', label: '资料', icon: 'IconUsers' },
    { id: 'security', label: '安全', icon: 'IconShield' },
    { id: 'pref', label: '偏好', icon: 'IconCog' },
    { id: 'sessions', label: '登录会话', icon: 'IconLayers' },
    { id: 'notify', label: '通知', icon: 'IconBell' },
    { id: 'tokens', label: 'API 令牌', icon: 'IconLock' },
  ];

  return (
    <div
      className="page page-enter"
      style={{ display: 'flex', gap: 20, padding: '16px 20px 28px', flexDirection: 'row' }}
    >
      <aside
        style={{ width: 240, flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start' }}
      >
        <h1 className="page-title" style={{ fontSize: 20, marginBottom: 12 }}>
          个人资料
        </h1>
        {/* Profile card */}
        <div className="card" style={{ padding: 14, marginBottom: 14 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: 'linear-gradient(135deg, oklch(0.6 0.18 35), oklch(0.45 0.14 320))',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              fontWeight: 600,
              marginBottom: 10,
            }}
          >
            {avatarInitial}
          </div>
          <div style={{ fontWeight: 500, fontSize: 13.5 }}>{user?.nickname ?? '—'}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
            {user?.email ?? '—'}
          </div>
          <div className="hstack" style={{ gap: 4, marginTop: 8 }}>
            <span className="chip chip-info" style={{ fontSize: 10 }}>
              {user ? (ROLE_LABEL[user.role] ?? user.role) : '—'}
            </span>
            {user?.totp_enabled && (
              <span className="chip" style={{ fontSize: 10 }}>
                双因子已启用
              </span>
            )}
          </div>
        </div>

        <div className="vstack" style={{ gap: 1 }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`nav-item ${tab === t.id ? 'active' : ''}`}
              style={{
                border: 0,
                font: 'inherit',
                textAlign: 'left',
                width: '100%',
                cursor: 'pointer',
                background: tab === t.id ? 'var(--bg-elev)' : 'transparent',
              }}
            >
              {createElement(Icons[t.icon], { size: 14 })}
              <span style={{ flex: 1 }}>{t.label}</span>
            </button>
          ))}
        </div>
      </aside>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          maxWidth: 720,
        }}
      >
        {tab === 'profile' && <Profile />}
        {tab === 'security' && <Security />}
        {tab === 'pref' && <Pref />}
        {tab === 'sessions' && <Sessions />}
        {tab === 'notify' && <Notify />}
        {tab === 'tokens' && <Tokens />}
      </div>
    </div>
  );
}

function Profile() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const avatarInitial = (user?.nickname || user?.email || '?').charAt(0).toUpperCase();
  // 显示名接真：PATCH /api/v1/me/profile（admin_users.nickname，admin 独有表、本人语义）→ 刷新 auth-store user。
  // 头像上传仍待文件服务（NOT-in-scope §五）；邮箱改动需验证链路（见 backlog B）→ 只读呈现。
  const [nickname, setNickname] = useState(user?.nickname ?? '');
  const nicknameTrimmed = nickname.trim();
  const dirty = !!user && nicknameTrimmed !== user.nickname;
  const valid = nicknameTrimmed.length >= 1 && nicknameTrimmed.length <= 64;
  const saveProfile = useMutation({
    mutationFn: () => meApi.updateProfile(nicknameTrimmed),
    onSuccess: (updated) => {
      setUser(updated);
      toast('资料已保存', { tone: 'pos', icon: 'IconCheck' });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '保存失败', { tone: 'neg', icon: 'IconAlert' }),
  });
  return (
    <Section
      title="基础资料"
      sub="账户基础信息 · 显示名可自助修改；邮箱由邀请时设定（改邮箱需验证链路，见计划 §五）"
      action={
        <button
          className="btn btn-accent btn-sm"
          disabled={!dirty || !valid || saveProfile.isPending}
          title={!dirty ? '无未保存修改' : !valid ? '显示名 1–64 字符' : undefined}
          style={!dirty || !valid ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
          onClick={() => saveProfile.mutate()}
        >
          <Icons.IconCheck size={11} />
          {saveProfile.isPending ? '保存中…' : '保存'}
        </button>
      }
    >
      {/* 头像接真（ADM-A9 自助）：存 admin 自有 BUCKET、经鉴权代理读取（ProfileAvatar authedFetch→blob）；
          未设头像回退首字母。上传走 POST /me/avatar（admin namespace，不碰 C 端 schema/桶）。 */}
      <FormRow label="头像" hint="首字母头像 · 可上传自定义头像（仅本人）">
        <ProfileAvatar initial={avatarInitial} />
      </FormRow>
      <FormRow label="显示名" hint="1–64 字符 · 用于后台展示与审计记录">
        <input
          className="input"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          maxLength={64}
          style={{ maxWidth: 280 }}
        />
      </FormRow>
      <FormRow label="邮箱" hint="登录与系统通知使用">
        <div className="hstack" style={{ gap: 8 }}>
          <input
            className="input mono"
            value={user?.email ?? ''}
            readOnly
            style={{ maxWidth: 280 }}
          />
          <span className="chip chip-pos" style={{ fontSize: 10 }}>
            已验证
          </span>
        </div>
      </FormRow>
      <FormRow label="角色">
        <span className="chip chip-info" style={{ fontSize: 10 }}>
          {user ? (ROLE_LABEL[user.role] ?? user.role) : '—'}
        </span>
      </FormRow>
    </Section>
  );
}

/** 新密码强度：基于长度 + 字符类别派生 5 档进度条 + 文案（前端提示，服务端 PasswordSchema 为权威） */
function passwordStrength(pw: string): { filled: number; label: string } {
  if (!pw) return { filled: 0, label: '—' };
  let score = 0;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  const label = score <= 2 ? '弱' : score <= 3 ? '中' : '强';
  return { filled: score, label };
}

function passwordSubmitHint(current: string, next: string, confirm: string): string | null {
  if (!current) return '请先填写当前密码';
  if (!next) return '请先填写新密码';
  if (next.length < 8 || !/[a-zA-Z]/.test(next) || !/[0-9]/.test(next)) {
    return '新密码至少 8 位，且需包含字母和数字';
  }
  if (next !== confirm) return '两次输入的新密码不一致';
  if (current === next) return '新密码不能与当前密码相同';
  return null;
}

function PasswordSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const queryClient = useQueryClient();
  const clearSession = useAuthStore((s) => s.clearSession);
  const strength = passwordStrength(next);
  // 改密合规预检（前端提示，服务端 ChangePasswordRequestSchema 为权威）：
  // new ≥12 含字母+数字、确认一致、当前非空、新旧不等
  const submitHint = passwordSubmitHint(current, next, confirm);
  const canSubmit = submitHint === null;

  const changeMutation = useMutation({
    mutationFn: () => meApi.changePassword(current, next),
    // 改密成功 → 服务端已 bump session_version + 撤销全会话（含本会话）：本地 access token 已失效。
    // 关键：不能再 POST /auth/logout —— 那一步会用已失效 token 触发 401（apiRequest 抛错），把成功
    // 流程转成 error（成功后又报错 + 跳转被跳过）。直接本地清会话即可：
    //   ① clearSession() 清 Zustand 登录态；② queryClient.clear() 清业务缓存（换账号防泄漏，
    //   与通用 logout 缓存治理一致，见 ~/lib/query-client）；③ window.location.assign 跳登录页。
    onSuccess: () => {
      toast('密码已更新 · 所有会话已失效，请重新登录', {
        tone: 'pos',
        icon: 'IconCheck',
        duration: 2400,
      });
      setCurrent('');
      setNext('');
      setConfirm('');
      window.setTimeout(() => {
        clearSession();
        queryClient.clear();
        window.location.assign('/auth/login');
      }, 900);
    },
    // 失败不假成功（当前密码错 → AUTH_INVALID_CREDENTIALS；弱密码 → VALIDATION_FAILED）；表单内容不丢
    onError: (err) => {
      toast(err instanceof Error ? err.message : '密码更新失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  const submitPasswordChange = () => {
    if (changeMutation.isPending) return;
    if (submitHint) {
      toast(submitHint, { tone: 'warn', icon: 'IconAlert' });
      return;
    }
    changeMutation.mutate();
  };

  return (
    <Section title="密码" sub="定期更换密码以保护账户安全 · 改密后所有会话立即失效需重新登录">
      <FormRow label="当前密码">
        <input
          className="input"
          type="password"
          placeholder="••••••••"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          style={{ maxWidth: 320 }}
        />
      </FormRow>
      <FormRow label="新密码" hint="至少 8 位 · 含字母 + 数字">
        <input
          className="input"
          type="password"
          placeholder="••••••••"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <div className="hstack" style={{ marginTop: 8, gap: 4 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                background: i < strength.filled ? 'var(--pos)' : 'var(--bg-muted)',
              }}
            />
          ))}
          <span style={{ fontSize: 11, color: 'var(--fg-muted)', marginLeft: 6 }}>
            强度：{strength.label}
          </span>
        </div>
      </FormRow>
      <FormRow label="确认新密码">
        <input
          className="input"
          type="password"
          placeholder="••••••••"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        {confirm.length > 0 && confirm !== next && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--neg)' }}>两次输入不一致</div>
        )}
      </FormRow>
      <div style={{ paddingTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-accent"
          disabled={changeMutation.isPending}
          aria-disabled={!canSubmit || changeMutation.isPending}
          title={submitHint ?? undefined}
          onClick={submitPasswordChange}
        >
          <Icons.IconCheck size={11} />
          更新密码
        </button>
      </div>
    </Section>
  );
}

/** 2FA 因子卡：TOTP 状态读当前 admin 真值；其它因子无端点时只展示诚实空态。*/
interface FactorDef {
  name: string;
  desc: string;
  icon: keyof typeof Icons;
  on: boolean;
  actionLabel: string;
  /** 接真的自助管理模式（TOTP 重绑 / 备用码重生成）；undefined = 维持 disabled（硬件/短信因子，选型仅 TOTP） */
  manageMode?: 'totp' | 'backup';
}

function TwoFactorSection() {
  const user = useAuthStore((s) => s.user);
  const totpEnabled = user?.totp_enabled === true;
  const factors: FactorDef[] = [
    {
      name: 'TOTP 验证器',
      desc: 'Google Authenticator / 1Password',
      icon: 'IconLock',
      on: totpEnabled,
      actionLabel: totpEnabled ? '重新绑定' : '绑定',
      // 已绑定 → 可自助重绑（轮转密钥）。未绑定（理论不出现：accept-invite 强制绑 TOTP）→ 无自助初绑流，维持 disabled。
      manageMode: totpEnabled ? 'totp' : undefined,
    },
    {
      name: '硬件密钥',
      desc: '硬件密钥因子端点未开放',
      icon: 'IconShield',
      on: false,
      actionLabel: '绑定',
    },
    {
      name: '短信验证',
      desc: '短信因子端点未开放',
      icon: 'IconMail',
      on: false,
      actionLabel: '绑定',
    },
    {
      name: '备用码',
      desc: 'TOTP 不可用时的一次性登录码',
      icon: 'IconCmd',
      on: totpEnabled,
      actionLabel: '重新生成',
      manageMode: 'backup',
    },
  ];
  // 自助 2FA 因子管理接真（ADM-A9）：TOTP 重绑 + 备用码重生成（manageMode）→ 打开 TwoFactorManageDialog。
  // 硬件密钥/短信因子（manageMode 未定）维持 disabled——鉴权选型仅 邮箱+密码+TOTP（INTENTIONAL_DESIGN）。
  // TOTP Switch 维持 disabled：2FA 强制（P0 §1），不提供解绑/禁用（关闭会降低账户安全性）。
  const [twoFaDialog, setTwoFaDialog] = useState<'backup' | 'totp' | null>(null);
  return (
    <>
      <Section
        title="双因子认证 (2FA)"
        sub="登录时除了密码外，还需要一次性验证码 · 绑定 / 解绑记入审计日志"
        action={
          <span
            className={`chip ${totpEnabled ? 'chip-pos' : 'chip-warn'}`}
            style={{ fontSize: 10 }}
          >
            <Icons.IconShield size={10} />
            {totpEnabled ? '已启用' : '未启用'}
          </span>
        }
      >
        <div
          style={{ padding: '14px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
        >
          {factors.map((m) => (
            <div key={m.name} className="card-flat" style={{ padding: 12 }}>
              <div className="hstack" style={{ gap: 10, marginBottom: 6 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: m.on ? 'var(--accent-soft)' : 'var(--bg-muted)',
                    color: m.on ? 'var(--accent)' : 'var(--fg-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {createElement(Icons[m.icon], { size: 14 })}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500 }}>{m.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{m.desc}</div>
                </div>
                {/* Switch 维持 disabled：2FA 强制（P0 §1），不提供解绑/禁用（关闭降低安全性）；因子管理走下方动作按钮。 */}
                <Switch on={m.on} disabled onChange={() => {}} />
              </div>
              <div className="hstack" style={{ gap: 6 }}>
                {/* manageMode 定义 → 接真自助管理（TOTP 重绑 / 备用码重生成）；否则维持 disabled（硬件/短信，选型仅 TOTP）。 */}
                <button
                  className="btn btn-sm"
                  disabled={!m.manageMode}
                  title={m.manageMode ? undefined : NOT_IN_SCOPE_TITLE}
                  style={m.manageMode ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                  onClick={() => m.manageMode && setTwoFaDialog(m.manageMode)}
                >
                  {m.actionLabel}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="单点登录 (SSO)" sub="使用公司域名身份登录">
        <FormRow label="SSO 状态">
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            本后台采用 邮箱+密码+TOTP 鉴权，未启用 SSO
          </div>
        </FormRow>
      </Section>

      {twoFaDialog && (
        <TwoFactorManageDialog mode={twoFaDialog} onClose={() => setTwoFaDialog(null)} />
      )}
    </>
  );
}

function Security() {
  return (
    <>
      <PasswordSection />
      <TwoFactorSection />
    </>
  );
}

function Pref() {
  const theme = useAppScope((s) => s.theme);
  const density = useAppScope((s) => s.density);
  const accent = useAppScope((s) => s.accent);
  const sidebarCollapsed = useAppScope((s) => s.sidebarCollapsed);
  const tablePageSize = useAppScope((s) => s.tablePageSize);
  const defaultAppId = useAppScope((s) => s.defaultAppId);
  const defaultPage = useAppScope((s) => s.defaultPage);
  const setTweak = useAppScope((s) => s.setTweak);
  // 默认 App 选项来自真实 App 列表（顶栏 AppSwitcher 同源）；默认页为主页面路由闭集
  const apps = useApps().data;
  const DEFAULT_PAGES = [
    { path: '/', label: '数据总览' },
    { path: '/books', label: '书籍管理' },
    { path: '/finance', label: '财务管理' },
    { path: '/users', label: '用户管理' },
    { path: '/feedback', label: '用户反馈' },
    { path: '/push', label: '推送管理' },
  ];

  const ACCENTS = [
    { label: 'Warm Amber', hue: 50, hex: 'oklch(0.68 0.17 50)' },
    { label: 'Forest', hue: 152, hex: 'oklch(0.68 0.17 152)' },
    { label: 'Indigo', hue: 245, hex: 'oklch(0.68 0.17 245)' },
    { label: 'Plum', hue: 320, hex: 'oklch(0.68 0.17 320)' },
  ];
  const current = ACCENTS.find((a) => a.hue === accent) ?? ACCENTS[0];
  if (!current) return null;
  return (
    <>
      <Section title="界面偏好" sub="所有偏好保存在本机 · 与右下角 Tweaks 联动">
        <FormRow label="主题" hint="跟随系统时随操作系统的浅色 / 深色自动切换 · 也可用顶栏 ☼/☽">
          <div className="seg">
            <button
              className={theme === 'light' ? 'on' : ''}
              onClick={() => setTweak({ theme: 'light' })}
            >
              <Icons.IconSun size={11} style={{ marginRight: 4 }} />
              浅色
            </button>
            <button
              className={theme === 'dark' ? 'on' : ''}
              onClick={() => setTweak({ theme: 'dark' })}
            >
              <Icons.IconMoon size={11} style={{ marginRight: 4 }} />
              深色
            </button>
            <button
              className={theme === 'system' ? 'on' : ''}
              onClick={() => setTweak({ theme: 'system' })}
            >
              <Icons.IconMonitor size={11} style={{ marginRight: 4 }} />
              跟随系统
            </button>
          </div>
        </FormRow>
        <FormRow label="主题色" hint={`当前 · ${current.label} (oklch hue ${current.hue})`}>
          <div className="hstack" style={{ gap: 8 }}>
            {ACCENTS.map((a) => {
              const active = accent === a.hue;
              return (
                <button
                  key={a.hue}
                  onClick={() => setTweak({ accent: a.hue })}
                  title={a.label}
                  style={{
                    padding: '6px 10px',
                    border: active ? '2px solid var(--accent)' : '1px solid var(--border)',
                    borderRadius: 6,
                    background: active ? 'var(--accent-soft)' : 'var(--bg-elev)',
                    cursor: 'pointer',
                    font: 'inherit',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      background: a.hex,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 11.5, color: 'var(--fg)' }}>{a.label}</span>
                  {a.hue === 50 && (
                    <span
                      className="mono"
                      style={{ fontSize: 9.5, color: 'var(--fg-faint)', letterSpacing: '0.04em' }}
                    >
                      DEFAULT
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </FormRow>
        <FormRow label="信息密度" hint="影响表格 / 卡片 / 按钮的紧凑度">
          <div className="seg">
            <button
              className={density === 'compact' ? 'on' : ''}
              onClick={() => setTweak({ density: 'compact' })}
            >
              紧凑
            </button>
            <button
              className={density === 'standard' ? 'on' : ''}
              onClick={() => setTweak({ density: 'standard' })}
            >
              标准
            </button>
            <button
              className={density === 'comfortable' ? 'on' : ''}
              onClick={() => setTweak({ density: 'comfortable' })}
            >
              宽松
            </button>
          </div>
        </FormRow>
        <FormRow label="侧边栏">
          <div className="seg">
            <button
              className={!sidebarCollapsed ? 'on' : ''}
              onClick={() => setTweak({ sidebarCollapsed: false })}
            >
              展开
            </button>
            <button
              className={sidebarCollapsed ? 'on' : ''}
              onClick={() => setTweak({ sidebarCollapsed: true })}
            >
              折叠
            </button>
          </div>
        </FormRow>
        <FormRow label="默认 App">
          <Select
            className="input"
            style={{ maxWidth: 240 }}
            value={defaultAppId}
            onChange={(e) => setTweak({ defaultAppId: e.target.value })}
          >
            <option value="all">登录时打开「全部 App」</option>
            {apps.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="默认页">
          <Select
            className="input"
            style={{ maxWidth: 240 }}
            value={defaultPage}
            onChange={(e) => setTweak({ defaultPage: e.target.value })}
          >
            {DEFAULT_PAGES.map((p) => (
              <option key={p.path} value={p.path}>
                {p.label}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="表格分页">
          <Select
            value={tablePageSize}
            onChange={(e) => setTweak({ tablePageSize: Number(e.target.value) as TablePageSize })}
            style={{ maxWidth: 140 }}
          >
            <option value={18}>18 行 / 页</option>
            <option value={30}>30 行 / 页</option>
            <option value={50}>50 行 / 页</option>
            <option value={100}>100 行 / 页</option>
          </Select>
        </FormRow>
      </Section>

      <Section title="键盘快捷键">
        <div style={{ padding: '12px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {(
            [
              ['全局搜索 / App 切换', '⌘ K'],
              ['切换主题', '⌘ J'],
              ['打开 Tweaks', '⌘ .'],
              ['收起侧边栏', '['],
              ['新建项', 'C'],
              ['回到 Dashboard', 'G D'],
              ['跳转书籍', 'G B'],
              ['跳转财务', 'G F'],
            ] as [string, string][]
          ).map(([l, k]) => (
            <div
              key={l}
              className="hstack"
              style={{
                padding: '6px 10px',
                gap: 8,
                background: 'var(--bg-subtle)',
                borderRadius: 5,
              }}
            >
              <span style={{ flex: 1, fontSize: 12 }}>{l}</span>
              {k.split(' ').map((ch, i) => (
                <span key={i} className="kbd">
                  {ch}
                </span>
              ))}
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

/** 距 expires_at 的剩余天数（向下取整，下限 0）；可信设备进度条 / 警示色用 */
function daysLeft(iso: string | null | undefined): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  return Number.isNaN(ms) ? 0 : Math.max(0, Math.floor(ms / 86_400_000));
}

function Sessions() {
  const { data: sessions, isLoading: sessionsLoading } = useMeSessions();
  const { data: devices, isLoading: devicesLoading } = useMeTrustedDevices();
  const queryClient = useQueryClient();
  const clearSession = useAuthStore((s) => s.clearSession);
  const [logoutAllOpen, setLogoutAllOpen] = useState(false);
  // 单会话登出 / 撤销设备的二次确认目标（受控 confirm，复刻 WhitelistDialog 模式）
  const [revokeSession, setRevokeSession] = useState<SelfSessionResponse | null>(null);
  const [revokeDevice, setRevokeDevice] = useState<TrustedDeviceResponse | null>(null);

  // 登出单会话：DELETE /me/sessions/:id → invalidate ['me-sessions']（前缀匹配，命中按 userId
  // 作用域的 key）。失败错误 toast 不假成功。
  // 当前会话不可辨识（契约 gap）：access token 载荷不带 session_id、SelfSessionResponse 亦无
  // `current` 标记（packages/shared 已注明 + 计划 §五）→ 无法在撤销前判断点的是不是「本会话」。
  // 防卡死兜底：单会话撤销不会 bump session_version（本 access token 仍有效），故不会立即 401；
  // 若运营撤销的恰是支撑本设备 refresh 的那条会话，access 过期后 refresh 会 401，apiRequest 自动清
  // 登录态并由路由守卫跳登录页——不会卡在「已撤销自己却仍停在页面」的死态。
  const revokeSessionMutation = useMutation({
    mutationFn: (id: string) => meApi.revokeSession(id),
    onSuccess: async () => {
      toast('已登出该会话', { tone: 'warn' });
      await queryClient.invalidateQueries({ queryKey: ['me-sessions'] });
    },
    onError: (err) => {
      // 若撤销的就是本会话且服务端已使本 token 失效（401）→ apiRequest 已清登录态，这里收尾跳登录页
      if (err instanceof AdminApiError && err.httpStatus === 401) {
        clearSession();
        window.location.assign('/auth/login');
        return;
      }
      toast(err instanceof Error ? err.message : '登出失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  // 撤销可信设备：DELETE /me/trusted-devices/:id → invalidate ['me-trusted-devices']
  const revokeDeviceMutation = useMutation({
    mutationFn: (id: string) => meApi.revokeTrustedDevice(id),
    onSuccess: async () => {
      toast('已撤销可信设备', { tone: 'warn' });
      await queryClient.invalidateQueries({ queryKey: ['me-trusted-devices'] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '撤销失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  // 登出所有设备：auth logout(true)（POST /auth/logout {all_devices:true}）→ session_version+1、
  // 全部会话立即失效（含本会话）→ 清本地 session + 跳登录页（不假成功）
  const logoutAllMutation = useMutation({
    mutationFn: () => logout(true),
    onSuccess: () => {
      toast('已登出所有设备 · 正在跳转登录页…', { tone: 'warn' });
      window.location.assign('/auth/login');
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '登出失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  const pending =
    revokeSessionMutation.isPending ||
    revokeDeviceMutation.isPending ||
    logoutAllMutation.isPending;

  // 本 tab 专属骨架：会话 / 可信设备列表尚未到达时，仅本 tab body 渲染骨架（页面 chrome 与其它 tab
  // 已立即可用，不被慢端点阻塞）。会话区 = 一组 Section 行骨架，可信设备区 = 表格骨架，与真实布局同形。
  if (sessionsLoading || devicesLoading) {
    return (
      <>
        <SectionSkeleton rows={2} />
        <TableSkeleton rows={3} cols={4} checkbox={false} />
      </>
    );
  }

  return (
    <>
      <Section
        title="登录会话"
        sub={`一个会话 = 一对 access/refresh token · 设备数：${sessions.length}`}
        action={
          <button
            className="btn btn-sm btn-danger"
            disabled={pending}
            onClick={() => setLogoutAllOpen(true)}
          >
            登出所有设备
          </button>
        }
      >
        <div style={{ padding: '12px 0' }}>
          {sessions.length === 0 ? (
            <div
              style={{
                padding: '24px 0',
                textAlign: 'center',
                fontSize: 12,
                color: 'var(--fg-muted)',
              }}
            >
              {sessionsLoading ? '加载中…' : '没有活跃会话'}
            </div>
          ) : (
            sessions.map((s, i) => (
              <div
                key={s.id}
                className="hstack"
                style={{
                  padding: '12px 0',
                  borderBottom: i < sessions.length - 1 ? '1px dashed var(--border)' : 0,
                  gap: 14,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 6,
                    background: 'var(--bg-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--fg-muted)',
                  }}
                >
                  <Icons.IconLayers size={15} />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="hstack" style={{ gap: 8 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                      {s.user_agent || '未知设备'}
                    </span>
                    {/* 「记住 14 天」会话 → 可信徽章（DTO remember 字段；access token 不带 session_id
                        无法标记「当前会话」，契约已注明本轮不做 current 推断）*/}
                    {s.remember && (
                      <span
                        className="chip chip-info"
                        style={{ fontSize: 10 }}
                        title="保持登录 · refresh TTL 延长"
                      >
                        <Icons.IconShield size={9} />
                        可信
                      </span>
                    )}
                  </div>
                  <div
                    className="mono"
                    style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}
                  >
                    {s.ip || '—'} · 最近活跃 {fmtDateTime(s.last_used_at) || '—'}
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-ghost"
                  style={{ color: 'var(--neg)' }}
                  disabled={pending}
                  onClick={() => setRevokeSession(s)}
                >
                  登出
                </button>
              </div>
            ))
          )}
        </div>
      </Section>

      <Section
        title="可信设备"
        sub="INFRA-AUTH-007 · 2FA 验证时勾选「记住 30 天」的设备 · 有效期内同设备登录跳过验证码 · 一设备 = 一个长期 device_token"
      >
        {devices.length >= 5 && (
          <div
            className="card-flat"
            style={{
              margin: '10px 0 0',
              padding: '8px 12px',
              background: 'var(--warn-soft)',
              borderColor: 'transparent',
            }}
          >
            <span style={{ fontSize: 11.5 }}>
              <Icons.IconAlert
                size={11}
                style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--warn)' }}
              />
              可信设备较多（≥5）· 超出上限时最早的设备将被自动撤销
            </span>
          </div>
        )}
        <div style={{ padding: '12px 0' }}>
          {devices.length === 0 ? (
            <div
              style={{
                padding: '24px 0',
                textAlign: 'center',
                fontSize: 12,
                color: 'var(--fg-muted)',
              }}
            >
              {devicesLoading
                ? '加载中…'
                : '没有可信设备 · 下次 2FA 验证时可勾选「在此设备记住 30 天」'}
            </div>
          ) : (
            <Table className="tbl">
              <thead>
                <tr>
                  <th>设备</th>
                  <th>最近使用</th>
                  <th>剩余有效期</th>
                  <th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => {
                  const left = daysLeft(d.expires_at);
                  return (
                    <tr key={d.id}>
                      <td>
                        <div className="hstack" style={{ gap: 8 }}>
                          <Icons.IconShield size={12} style={{ color: 'var(--info)' }} />
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 500 }}>
                              {d.user_agent || '未知设备'}
                            </div>
                            <div
                              className="mono"
                              style={{ fontSize: 10, color: 'var(--fg-faint)' }}
                            >
                              {d.id.slice(0, 8)} · {d.ip || '—'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                        {fmtDateTime(d.last_used_at) || '—'}
                      </td>
                      <td>
                        <div className="hstack" style={{ gap: 8 }}>
                          <div
                            style={{
                              width: 70,
                              height: 4,
                              borderRadius: 2,
                              background: 'var(--bg-muted)',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${(Math.min(left, 30) / 30) * 100}%`,
                                height: '100%',
                                background: left <= 7 ? 'var(--warn)' : 'var(--accent)',
                              }}
                            />
                          </div>
                          <span
                            className="mono"
                            style={{
                              fontSize: 11,
                              color: left <= 7 ? 'var(--warn)' : 'var(--fg-muted)',
                            }}
                          >
                            {left} 天
                          </span>
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-ghost"
                          style={{ color: 'var(--neg)' }}
                          disabled={pending}
                          onClick={() => setRevokeDevice(d)}
                        >
                          撤销
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </Section>

      {logoutAllOpen && (
        <ConfirmDialog
          title="登出所有设备？"
          message="此操作会在所有设备上登出，包括当前设备（session_version+1 · 全部会话立即失效）。确认后需重新登录。"
          confirmLabel="登出全部设备"
          tone="danger"
          requireTyping="登出全部"
          onConfirm={() => logoutAllMutation.mutate()}
          onClose={() => setLogoutAllOpen(false)}
        />
      )}
      {revokeSession && (
        <ConfirmDialog
          title="登出该会话？"
          message={`${revokeSession.user_agent || '未知设备'} · ${revokeSession.ip || '—'} 将被立即登出，该设备的 refresh token 失效。`}
          confirmLabel="确认登出"
          tone="warn"
          onConfirm={() => revokeSessionMutation.mutate(revokeSession.id)}
          onClose={() => setRevokeSession(null)}
        />
      )}
      {revokeDevice && (
        <ConfirmDialog
          title="撤销可信设备？"
          message={`撤销后 ${revokeDevice.user_agent || '该设备'} 下次登录将重新要求 2FA 验证码。`}
          confirmLabel="确认撤销"
          tone="warn"
          onConfirm={() => revokeDeviceMutation.mutate(revokeDevice.id)}
          onClose={() => setRevokeDevice(null)}
        />
      )}
    </>
  );
}

// 通知类型 taxonomy + 各渠道默认值由前端拥有（后端 admin_notification_prefs 仅按 typeKey 存覆盖值）；
// 新增类型在此加一行即可，无需改后端 schema。
const NOTIFY_ROWS = [
  {
    key: 'feedback_assigned',
    name: '反馈被分配给我',
    desc: '新工单或转派到我',
    email: true,
    inApp: true,
    push: false,
  },
  {
    key: 'push_task_done',
    name: '我创建的推送任务完成',
    desc: '送达后总结',
    email: true,
    inApp: true,
    push: true,
  },
  {
    key: 'anomaly_alert',
    name: '异常告警 (财务/风控)',
    desc: '关键阈值触发',
    email: true,
    inApp: true,
    push: true,
  },
  {
    key: 'member_online',
    name: '团队成员上线提醒',
    desc: '新成员加入工作区',
    email: false,
    inApp: true,
    push: false,
  },
  {
    key: 'weekly_report',
    name: '周报',
    desc: '每周一 09:00 邮件汇总',
    email: true,
    inApp: false,
    push: false,
  },
] as const;

type NotifyRow = (typeof NOTIFY_ROWS)[number];
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function defaultChannels(r: NotifyRow): NotificationChannelPrefs {
  return { email: r.email, in_app: r.inApp, push: r.push };
}

interface NotifyForm {
  prefs: Record<string, NotificationChannelPrefs>;
  dndEnabled: boolean;
  dndStart: string;
  dndEnd: string;
}

/**
 * 通知偏好 + 勿扰时段（接真）：
 *  - 读 GET /api/v1/me/notification-preferences → 合并前端默认值回填编辑态
 *  - 改 PATCH /api/v1/me/notification-preferences（PATCH 语义，本人 scope）→ 成功 invalidate + toast
 */
function Notify() {
  const { data: saved, isLoading } = useMeNotificationPrefs();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<NotifyForm | null>(null);

  // saved 加载后 / 保存回流后：用已持久化覆盖值合并默认值，重置本地编辑态。
  useEffect(() => {
    if (isLoading) return;
    const merged: Record<string, NotificationChannelPrefs> = {};
    for (const r of NOTIFY_ROWS) merged[r.key] = saved.prefs[r.key] ?? defaultChannels(r);
    setForm({
      prefs: merged,
      dndEnabled: saved.dnd_enabled,
      dndStart: saved.dnd_start ?? '22:00',
      dndEnd: saved.dnd_end ?? '08:00',
    });
  }, [isLoading, saved]);

  const saveMutation = useMutation({
    mutationFn: (input: UpdateNotificationPrefsRequest) => meApi.updateNotificationPrefs(input),
    onSuccess: async () => {
      toast('通知偏好已保存', { tone: 'pos', icon: 'IconCheck' });
      await queryClient.invalidateQueries({ queryKey: ['me-notification-prefs'] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '保存失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  if (!form) return <SectionSkeleton />;

  const setChannel = (key: string, ch: keyof NotificationChannelPrefs, on: boolean) =>
    setForm((f) => {
      if (!f) return f;
      const cur = f.prefs[key] ?? { email: false, in_app: false, push: false };
      const updated: NotificationChannelPrefs = { ...cur, [ch]: on };
      return { ...f, prefs: { ...f.prefs, [key]: updated } };
    });

  const dndValid = !form.dndEnabled || (HHMM_RE.test(form.dndStart) && HHMM_RE.test(form.dndEnd));

  const save = () => {
    if (!dndValid) {
      toast('勿扰时段需为 HH:mm', { tone: 'warn', icon: 'IconAlert' });
      return;
    }
    saveMutation.mutate({
      prefs: form.prefs,
      dnd_enabled: form.dndEnabled,
      dnd_start: form.dndEnabled ? form.dndStart : null,
      dnd_end: form.dndEnabled ? form.dndEnd : null,
    });
  };

  return (
    <>
      <Section
        title="通知偏好"
        sub="选择你希望在哪些渠道接收通知"
        action={
          <button
            className="btn btn-accent btn-sm"
            disabled={saveMutation.isPending || !dndValid}
            onClick={save}
          >
            <Icons.IconCheck size={11} />
            {saveMutation.isPending ? '保存中…' : '保存'}
          </button>
        }
      >
        <Table className="tbl" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>类型</th>
              <th style={{ width: 70, textAlign: 'center' }}>邮件</th>
              <th style={{ width: 70, textAlign: 'center' }}>站内</th>
              <th style={{ width: 70, textAlign: 'center' }}>移动推送</th>
            </tr>
          </thead>
          <tbody>
            {NOTIFY_ROWS.map((r) => {
              const ch = form.prefs[r.key] ?? defaultChannels(r);
              return (
                <tr key={r.key}>
                  <td>
                    <div style={{ fontSize: 12.5, fontWeight: 500 }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{r.desc}</div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ display: 'inline-flex' }}>
                      <Switch on={ch.email} onChange={(v) => setChannel(r.key, 'email', v)} />
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ display: 'inline-flex' }}>
                      <Switch on={ch.in_app} onChange={(v) => setChannel(r.key, 'in_app', v)} />
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ display: 'inline-flex' }}>
                      <Switch on={ch.push} onChange={(v) => setChannel(r.key, 'push', v)} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Section>

      <Section title="勿扰时段" sub="非紧急通知会延迟到时段结束后送达">
        <FormRow label="开启勿扰">
          <Switch
            on={form.dndEnabled}
            onChange={(v) => setForm((f) => (f ? { ...f, dndEnabled: v } : f))}
          />
        </FormRow>
        <FormRow label="时段">
          <div className="hstack" style={{ gap: 8 }}>
            <input
              className="input mono"
              value={form.dndStart}
              disabled={!form.dndEnabled}
              onChange={(e) => setForm((f) => (f ? { ...f, dndStart: e.target.value } : f))}
              placeholder="22:00"
              style={{ width: 90, textAlign: 'center' }}
            />
            <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>—</span>
            <input
              className="input mono"
              value={form.dndEnd}
              disabled={!form.dndEnabled}
              onChange={(e) => setForm((f) => (f ? { ...f, dndEnd: e.target.value } : f))}
              placeholder="08:00"
              style={{ width: 90, textAlign: 'center' }}
            />
            <span style={{ color: 'var(--fg-muted)', fontSize: 11, marginLeft: 8 }}>
              个人时区 · Europe/Berlin
            </span>
          </div>
        </FormRow>
      </Section>
    </>
  );
}

function Tokens() {
  // 个人 API Token 无后端契约（签发 / 列表 / 撤销端点均缺）→ NOT-in-scope（计划 §五）。
  // 此前渲染硬编码 token（名 / 前缀 / scope / 最近用 / 过期）= 假的安全清单，会误导运营把杜撰
  // 数据当真存在的凭证去管理（adversarial F4）。改为诚实空态：不渲染任何令牌行，明示契约未提供。
  return (
    <Section
      title="API 令牌"
      sub="个人 API Token · 用于脚本与 BI · Token 端点未开放（见计划 §五）"
      action={
        <button
          className="btn btn-accent btn-sm"
          disabled
          title={NOT_IN_SCOPE_TITLE}
          style={{ opacity: 0.55, cursor: 'not-allowed' }}
        >
          <Icons.IconPlus size={11} />
          新建令牌
        </button>
      }
    >
      <div
        style={{
          padding: '28px 0',
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--fg-muted)',
        }}
        title={NOT_IN_SCOPE_TITLE}
      >
        <Icons.IconLock
          size={18}
          style={{ color: 'var(--fg-faint)', marginBottom: 8, display: 'inline-block' }}
        />
        <div>令牌契约未提供 · 个人 API Token 签发 / 列表 / 撤销端点尚未开放（见计划 §五）</div>
      </div>
    </Section>
  );
}
